import React, { useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { InputAdornment, MenuItem, Popover, Skeleton, TextField, Typography } from "@mui/material";
import { getClasses } from "shared/helpers/styleHelper";
import { MagnifyingGlassIcon } from "shared/icons/MagnifyingGlassIcon";
import { KababMenu } from "../Menu/KababMenu";
import { AdjustmentIcon } from "shared/icons/AdjustmentIcon";
import { BlockTypes } from "modules/MainSurveyEditor/helpers/blocks";
import { BlockIcon } from "../BlockOption/BlockIcon";
import { ChevronIcon } from "shared/icons/ChevronArrIcon";

export const SelectWithSearchAndFilters = (props: SelectWithSearchAndFiltersProps) => {
  const {
    selectedOption,
    selectedFilter,
    parentValue,
    isOptionsLoading,
    options,
    parentOptions,
    filterOptions,
    handleParentChange,
    isParentLoading,
    goBackAction,
    className,
    placeholder,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const filteredOptions = options.filter((o) => o.label.toLowerCase().includes(searchValue.toLowerCase()));

  const selectClasses = getClasses({
    "module-select-with-search-and-filters": true,
    "is-open": isOpen,
  });

  return (
    <>
      <div className={selectClasses + " " + className} onClick={handleClick}>
        {selectedOption?.value ? (
          <Typography variant="h4" color="text.secondary">
            {selectedOption?.label}
          </Typography>
        ) : (
          <Typography variant="h4" color="text.tertiary">
            {placeholder || "select"}
          </Typography>
        )}
        <ArrowDropDownIcon />
      </div>

      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        slotProps={{ paper: { className: "module-select-with-search-and-filters-menu" } }}
      >
        <TextField
          placeholder="Search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 1 }}>
                <MagnifyingGlassIcon />
              </InputAdornment>
            ),
          }}
        />

        <div className="_d-flex-ali-center-jc-sb-md-gap filter-line">
          {!!parentOptions &&
            !!handleParentChange &&
            (isParentLoading ? (
              <Skeleton sx={{ flex: 1 }} />
            ) : (
              <TextField
                select
                value={parentValue}
                onChange={(e) => handleParentChange(e.target.value)}
                variant="standard"
                className="is-hide-input-border"
                inputProps={{ sx: { px: 1 } }}
                disabled
              >
                {parentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}

          {filterOptions && (
            <KababMenu
              icon={<AdjustmentIcon />}
              closeOnClick
              items={filterOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={option.onSelect ? () => option.onSelect!(option.value) : undefined}
                  selected={option.value === selectedFilter}
                >
                  {option.label}
                </MenuItem>
              ))}
            />
          )}
        </div>

        <div className="menu-options">
          {goBackAction && (
            <MenuItem onClick={goBackAction.onClick} className="_d-flex-ali-center-sm-gap">
              <ChevronIcon direction="left" />
              <Typography>{goBackAction.title}</Typography>
            </MenuItem>
          )}
          {isOptionsLoading ? (
            <div className="_d-grid-sm-gap">
              <Skeleton />
              <Skeleton width="80%" />
            </div>
          ) : (
            filteredOptions.map((option, i) => {
              let onClick;
              if (option.onExpand) {
                onClick = () => option.onExpand!(option.value);
              } else if (option.onSelect) {
                onClick = () => {
                  option.onSelect!(option.value);
                  handleClose();
                };
              }

              return (
                <MenuItem
                  key={i}
                  onClick={onClick}
                  className="_d-flex-ali-center-sm-gap"
                  sx={{ justifyContent: "space-between", px: 0.5 }}
                  selected={option.value === selectedOption?.value && option.blockType === selectedOption?.blockType}
                >
                  <div className="_d-flex-ali-center-sm-gap">
                    {option.blockType && <BlockIcon blockType={option.blockType} />}
                    <Typography>{option.label}</Typography>
                  </div>
                  {option.onExpand && <ChevronIcon direction="right" />}
                </MenuItem>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
};

interface SelectWithSearchAndFiltersProps {
  isOptionsLoading: boolean;
  options: Option[];
  parentValue?: string;
  parentOptions?: ShallowOption[];
  handleParentChange?: (value: string) => void;
  selectedOption?: ShallowOption;
  selectedFilter?: string;
  isParentLoading?: boolean;
  filterOptions?: Option[];
  goBackAction?: { onClick: () => void; title: string };
  className?: string;
  placeholder?: string;
}

interface ShallowOption {
  value: string;
  label: string;
  blockType?: BlockTypes;
}

interface Option extends ShallowOption {
  onExpand?: (value: string) => void;
  onSelect?: (value: string) => void;
}
